import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackSection = ({ page = "general" }) => {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/feedback`,
        {
          feedback_text: feedback,
          page: page
        },
        { withCredentials: true }
      );

      toast.success("Thank you for your feedback! 🙏");
      setSubmitted(true);
      setFeedback("");
      
      // Reset submitted state after 3 seconds
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg border-2 border-gray-200 mt-8" data-testid="feedback-section">
      <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-blue-600" />
          <CardTitle className="text-xl">We Value Your Feedback</CardTitle>
        </div>
        <CardDescription>
          Help us improve AI Newsletter - share your thoughts, suggestions, or issues
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-900 mb-2">
              Feedback Received!
            </h3>
            <p className="text-gray-600">
              Thank you for helping us improve AI Newsletter
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <Textarea
              data-testid="feedback-textarea"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Please insert your feedback here, what you want to be improved..."
              className="min-h-[120px] text-base placeholder:text-gray-400"
              disabled={loading}
            />
            
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Your feedback helps us make AI Newsletter better for everyone
              </p>
              <Button
                data-testid="submit-feedback-btn"
                onClick={handleSubmit}
                disabled={loading || !feedback.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Send className="mr-2 h-4 w-4 animate-pulse" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackSection;
